import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** `DataTable` 表头行高（其内联 style 为 `height: 50px`） */
const DATA_TABLE_HEADER_HEIGHT = 50;
/**
 * `DataTable` 无数据时的占位行高
 * 对应 `<TableRow><TableCell colSpan={columns.length} className="h-24" /></TableRow>`
 */
const DATA_TABLE_EMPTY_ROW_HEIGHT = 96;

export interface ListPageSkeletonProps {
  /** 是否绘制卡片内的层级 Tab 行（如创编管理的优化师 / 广告账户 / 广告系列…） */
  withHeaderTabs?: boolean;
  /** 层级 Tab 占位数量 */
  headerTabCount?: number;
  /**
   * 表格占位列数
   * @default 7（对齐草稿箱 / 策略模板：名称 + 4 个信息列 + 备注 + 操作；其它页面按真实列数显式传入）
   */
  columnCount?: number;
  /** 自定义筛选及操作区，不传时使用通用占位布局 */
  filterArea?: ReactNode;
  /** 是否显示分页占位，首次加载尚无分页的页面可关闭 */
  showPagination?: boolean;
  /** 内容区样式，用于对齐页面实际内边距和区块间距 */
  contentClassName?: string;
  className?: string;
}

/**
 * 列表页骨架：筛选行 + 表头 + 单个空行 + 分页。
 *
 * 高度对齐首次加载时的空表：`DataTable` 尚无数据时，
 * 表格体只渲染一个 `h-24`（96px）的占位行，数据到达后才会增长。
 * 如果骨架屏预填很多行把页面撑满，就会出现「骨架屏撑满 → 加载中塌下去 → 数据到达又撑回来」
 * 的上下跳动，所以这里只画一个空行，让骨架屏 → 加载中这一步无缝衔接，
 * 后续的增长交给应用自身的加载行为。
 */
export function ListPageSkeleton({
  withHeaderTabs = false,
  headerTabCount = 5,
  columnCount = 7,
  filterArea,
  showPagination = true,
  contentClassName,
  className,
}: ListPageSkeletonProps) {
  return (
    <Card data-testid="list-page-skeleton" className={cn('w-full p-0', className)}>
      <CardContent className={cn('relative flex h-auto flex-col gap-2 p-4', contentClassName)}>
        {/** 层级 Tab 行 */}
        {withHeaderTabs && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4 border-b pb-2">
              {Array.from({ length: headerTabCount }, (_, index) => (
                <Skeleton key={index} className="h-5 w-14" />
              ))}
            </div>
          </div>
        )}

        {/** 筛选行（左侧条件 + 右侧操作按钮） */}
        {filterArea ?? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-9 w-44" />
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-9 w-36" />
              <div className="ml-auto flex flex-row items-center gap-2">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          </div>
        )}

        {/** 表格：表头 + 单个空行（对齐 DataTable 的加载 / 空态高度） */}
        <div>
          <div
            className="flex items-center gap-4 border-y bg-muted px-4"
            style={{ height: DATA_TABLE_HEADER_HEIGHT }}
          >
            {/** 注意不能直接用 Skeleton 的默认底色：本主题 --muted 与 --accent 取值相同，
                块会与表头底色完全重合、列分割看不见，所以显式给一层对比色 */}
            {Array.from({ length: columnCount }, (_, index) => (
              <Skeleton key={index} className="h-4 flex-1 bg-[#e4e4e4] dark:bg-[#3a3a3a]" />
            ))}
          </div>
          <div className="border-b" style={{ height: DATA_TABLE_EMPTY_ROW_HEIGHT }} />
        </div>

        {/** 分页 */}
        {showPagination && (
          <div className="flex items-center justify-between pt-2">
            <Skeleton className="h-5 w-28" />
            <div className="flex items-center gap-2">
              {Array.from({ length: 4 }, (_, index) => (
                <Skeleton key={index} className="size-8" />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
